import { BaseModel } from './BaseModel';
import Forum from './Forum';
import ForumLiker from './ForumLiker';
import ForumThreadUser from './ForumThreadUser';
import { datetimeMixin } from '@/mixins/datetimeMixin';

export class ForumThreads extends BaseModel {
  static get entity() {
    return 'forum-threads';
  }

  static get fk() {
    return 'forum_thread_id';
  }

  static fields() {
    return {
      id: this.attr(null),
      content: this.attr(null),
      count_replies: this.number(0),
      count_likes: this.number(0),
      forum_id: this.attr(null),
      forum: this.belongsTo(Forum, 'forum_id'),
      images: this.attr([]),
      user_id: this.attr(null),
      user: this.belongsTo(ForumThreadUser, 'user_id'),
      main_forum_thread_id: this.attr(null),
      main_forum_thread: this.belongsTo(ForumThreads, 'main_forum_thread_id'),
      liked_id: this.attr(null),
      liked: this.belongsTo(ForumLiker, 'liked_id'),
      created_at: this.attr(null),
      updated_at: this.attr(null),
      edited_at: this.attr(null)
    };
  }

  isOwner(userId) {
    return userId === this.user_id;
  }

  static apiConfig = {
    actions: {
      async fetchForumThreadsMeta(forumId) {
        return this.get(`/forums/${forumId}/threadsMeta`);
      },
      async fetchReplyThreadsMeta(threadId) {
        return this.get(`/forum-threads/${threadId}/replyThreadsMeta`);
      },

      async fetchForumThreads(
        forumId,
        startAt = null,
        endAt = null,
        chunkIndex = 0,
        limit = 5,
        sort = 'asc'
      ) {
        let filter = {
          startAt,
          endAt,
          chunkIndex,
          limit,
          sort
        };
        return this.get(`/forums/${forumId}/threads`, {
          params: filter
        });
      },
      async fetchReplyThreads(
        mainThreadId,
        startAt = null,
        endAt = null,
        chunkIndex = 0,
        limit = 5,
        sort = 'asc'
      ) {
        let filter = {
          startAt,
          endAt,
          chunkIndex,
          limit,
          sort
        };
        return this.get(`/forum-threads/${mainThreadId}/replyThreads`, {
          params: filter
        });
      },

      async getForumThreadById(forumId, forumThreadId) {
        return this.get(`/forums/${forumId}/thread/${forumThreadId}`);
      },

      async createForumThread(forumId, body) {
        return this.post(`/forums/${forumId}/createThread`, body);
      },

      async createReplyThread(forumThreadId, body) {
        return this.post(`/forum-threads/${forumThreadId}/createReplyThread`, body);
      },

      async updateForumThread(forumId, forumThreadId, body) {
        return this.put(`/forums/${forumId}/editThread/${forumThreadId}`, body);
      },

      async deleteCommentThread(forumId, forumThreadId) {
        return this.delete(`/forums/${forumId}/deleteThread/${forumThreadId}`, {
          delete: forumThreadId
        });
      },

      async deleteReplyThread(forumThreadId, replyThreadId) {
        return this.delete(`/forum-threads/${forumThreadId}/deleteReplyThread/${replyThreadId}`, {
          delete: replyThreadId
        });
      },

      likeForumThread(forumThreadId) {
        return this.put(`/forum-threads/${forumThreadId}/like`);
      },
      unlikeForumThread(forumThreadId) {
        return this.put(`/forum-threads/${forumThreadId}/unlike`);
      }
    }
  };

  get timestamp() {
    if (this.edited_at && this.created_at !== this.edited_at) {
      return datetimeMixin.methods.formatDynamicDateTime(new Date(this.created_at)) + ' (edited)';
    } else {
      return datetimeMixin.methods.formatDynamicDateTime(new Date(this.created_at));
    }
  }

  get isReplyThread() {
    return this.main_forum_thread_id !== null;
  }

  async delete() {
    let response;

    if (this.isReplyThread) {
      response = await ForumThreads.api().deleteReplyThread(this.main_forum_thread_id, this.id);
    } else {
      response = await ForumThreads.api().deleteCommentThread(this.forum_id, this.id);

      ForumThreads.delete((thread) => {
        return thread.main_forum_thread_id !== null && thread.main_forum_thread_id == this.id;
      });
    }

    return response;
  }
}
export default ForumThreads;
